import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  useConfirmBrokeragePlanCheckout,
  useManagedBrokerage,
  useOpenBrokeragePlanPortal,
  useSaveBrokerageSeats,
  useStartBrokeragePlanCheckout,
} from "@/hooks/useBrokerageManage";
import { usePackage } from "@/hooks/usePlans";
import {
  money,
  packageActiveFor,
  planReturnOf,
  seatsParamOf,
  totalsOf,
  yearlyLabelOf,
} from "./brokeragePlanModel";
import "./BrokeragePlan.scss";

const clearReturn = () => {
  const url = new URL(window.location.href);
  ["plan", "session_id", "seats"].forEach((name) =>
    url.searchParams.delete(name),
  );
  window.history.replaceState(
    window.history.state,
    "",
    `${url.pathname}${url.search}${url.hash}`,
  );
};

const go = (url) => window.location.assign(url);

const dateOf = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : "";

const SeatPicker = ({ agents, seats, onChange, price }) => (
  <fieldset className="brokerage-plan__seats">
    <legend>Who gets Gitcha Pro from the office</legend>
    <p className="brokerage-plan__hint">
      {price ? `${money(price)} each a month. ` : ""}Agents who asked you get
      their own Pro refunded for the days left. Anyone else keeps theirs until it&nbsp;renews.
    </p>
    {agents.length ? (
      <ul className="brokerage-plan__agents">
        {agents.map((agent) => {
          const checked = seats.includes(agent.userId);
          return (
            <li key={agent.userId}>
              <label className="brokerage-plan__agent">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() =>
                    onChange(
                      checked
                        ? seats.filter((id) => id !== agent.userId)
                        : [...seats, agent.userId],
                    )
                  }
                />
                <span>{agent.name}</span>
              </label>
            </li>
          );
        })}
      </ul>
    ) : (
      <p className="brokerage-plan__hint">
        Agents on your brokerage page show here.
      </p>
    )}
  </fieldset>
);

const BrokeragePlan = ({ brokerageId }) => {
  const query = useManagedBrokerage(brokerageId);
  const data = query.data;
  const { pkg } = usePackage("brokerage");
  const price = pkg?.price || null;
  const start = useStartBrokeragePlanCheckout(brokerageId);
  const portal = useOpenBrokeragePlanPortal(brokerageId);
  const confirm = useConfirmBrokeragePlanCheckout(brokerageId);
  const saveSeats = useSaveBrokerageSeats(brokerageId);
  const [period, setPeriod] = useState("month");
  const [seats, setSeats] = useState(() =>
    seatsParamOf(window.location.search),
  );
  const [editingSeats, setEditingSeats] = useState(false);
  const [result, setResult] = useState(null);
  const handled = useRef(null);

  useEffect(() => {
    const back = planReturnOf(window.location.search);
    if (!back) return;
    if (back.outcome === "canceled") {
      clearReturn();
      return;
    }
    if (!back.sessionId || handled.current === back.sessionId) return;
    handled.current = back.sessionId;
    setResult("confirming");
    confirm.mutate(back.sessionId, {
      onSuccess: () => setResult("done"),
      onError: () => setResult("failed"),
      onSettled: clearReturn,
    });
  }, []);

  const plan = data?.plan || data?.plus || null;
  const active = Boolean(plan?.active || packageActiveFor(pkg, brokerageId));
  const agents = data?.agents || [];

  useEffect(() => {
    if (active && plan?.seatUserIds && !editingSeats)
      setSeats(plan.seatUserIds);
  }, [active, JSON.stringify(plan?.seatUserIds)]);

  if (!data && !query.isError) {
    return (
      <section className="content-block brokerage-plan" aria-busy="true">
        <div className="title-sec">
          <h4>Brokerage plan</h4>
        </div>
        <div className="brokerage-plan__skeleton" />
      </section>
    );
  }

  const yearly = Number.isFinite(Number(price?.yearly));
  const totals = totalsOf({ price, period, seats: seats.length });
  const pay = () =>
    start.mutate(
      { period, seatUserIds: seats },
      {
        onSuccess: (response) =>
          response?.url
            ? go(response.url)
            : toast.error("Checkout did not open. Try again."),
        onError: (error) => toast.error(error.message),
      },
    );
  const manage = () =>
    portal.mutate(undefined, {
      onSuccess: (response) =>
        response?.url
          ? go(response.url)
          : toast.error("Billing did not open. Try again."),
      onError: (error) => toast.error(error.message),
    });
  const saveSeatChanges = () =>
    saveSeats.mutate(seats, {
      onSuccess: () => {
        setEditingSeats(false);
        toast.success("Pro seats are updated. Changes are prorated.");
      },
      onError: (error) => toast.error(error.message),
    });

  return (
    <section
      className="content-block brokerage-plan"
      aria-labelledby="brokerage-plan-title"
    >
      <div className="title-sec">
        <h4 id="brokerage-plan-title">
          Brokerage plan{data?.name ? ` for ${data.name}` : ""}
        </h4>
      </div>

      {result === "confirming" ? (
        <p className="brokerage-plan__notice" role="status">
          Confirming your payment with&nbsp;Stripe…
        </p>
      ) : null}
      {result === "done" ? (
        <p className="brokerage-plan__notice is-good" role="status">
          Your brokerage plan is on. Open the Brokerage tab to put your buyers
          on your&nbsp;website.
        </p>
      ) : null}
      {result === "failed" ? (
        <p className="brokerage-plan__notice is-bad" role="alert">
          We could not confirm your payment yet. If Stripe charged your card, it
          shows here within a few&nbsp;minutes.
        </p>
      ) : null}

      {active ? (
        <>
          <div className="brokerage-plan__status">
            <dl className="brokerage-plan__stats">
              {plan?.amount || price?.monthly ? (
                <div>
                  <dt>Price</dt>
                  <dd>{`${money(plan?.amount || price.monthly)} a ${plan?.period === "year" ? "year" : "month"}`}</dd>
                </div>
              ) : null}
              <div>
                <dt>Pro seats</dt>
                <dd>{(plan?.seatUserIds || []).length}</dd>
              </div>
              {plan?.renewsAt ? (
                <div>
                  <dt>Renews</dt>
                  <dd>{dateOf(plan.renewsAt)}</dd>
                </div>
              ) : null}
            </dl>
            <span className="brokerage-plan__status-actions">
              <button
                type="button"
                className="brokerage-plan__link"
                onClick={() => setEditingSeats(!editingSeats)}
              >
                {editingSeats ? "Cancel" : "Change Pro seats"}
              </button>
              <button
                type="button"
                className="brokerage-plan__link"
                disabled={portal.isPending}
                onClick={manage}
              >
                {portal.isPending ? "Opening…" : "Manage billing"}
              </button>
            </span>
          </div>
          {editingSeats ? (
            <div className="brokerage-plan__edit">
              <SeatPicker
                agents={agents}
                seats={seats}
                onChange={setSeats}
                price={price?.seat}
              />
              <button
                type="button"
                className="btn btn-primary"
                disabled={saveSeats.isPending}
                onClick={saveSeatChanges}
              >
                {saveSeats.isPending ? "Saving…" : "Save Pro seats"}
              </button>
            </div>
          ) : null}
        </>
      ) : (
        <div className="brokerage-plan__checkout">
          <div className="brokerage-plan__choices">
            <p className="brokerage-plan__lead">
              Your agents&apos; Buyer Want&#8209;Listings on your company
              website, each lead sent to the right agent, Office analytics and
              your logo on Demand&nbsp;Reports.
            </p>
            <SeatPicker
              agents={agents}
              seats={seats}
              onChange={setSeats}
              price={price?.seat}
            />
            <fieldset className="brokerage-plan__billing">
              <legend>Billing</legend>
              <label
                className={`brokerage-plan__option${period === "month" ? " is-on" : ""}`}
              >
                <input
                  type="radio"
                  name="brokerage-plan-period"
                  checked={period === "month"}
                  onChange={() => setPeriod("month")}
                />
                <span>Monthly</span>
                <span className="brokerage-plan__amount">
                  {price?.monthly ? `${money(price.monthly)} a month` : ""}
                </span>
              </label>
              {yearly ? (
                <label
                  className={`brokerage-plan__option${period === "year" ? " is-on" : ""}`}
                >
                  <input
                    type="radio"
                    name="brokerage-plan-period"
                    checked={period === "year"}
                    onChange={() => setPeriod("year")}
                  />
                  <span>{yearlyLabelOf(price)}</span>
                  <span className="brokerage-plan__amount">{`${money(price.yearly)} a year`}</span>
                </label>
              ) : null}
            </fieldset>
          </div>
          <aside className="brokerage-plan__summary" aria-label="Order summary">
            <h5>{period === "year" ? "Each year" : "Each month"}</h5>
            {totals ? (
              <>
                <p className="brokerage-plan__line">
                  <span>Brokerage plan</span>
                  <span>{money(totals.office)}</span>
                </p>
                <p className="brokerage-plan__line">
                  <span>
                    Gitcha Pro, {seats.length}{" "}
                    {seats.length === 1 ? "agent" : "agents"}
                  </span>
                  <span>{money(totals.seatsTotal)}</span>
                </p>
                <p className="brokerage-plan__line is-total">
                  <span>Billed today</span>
                  <span>{money(totals.total)}</span>
                </p>
              </>
            ) : (
              <div className="brokerage-plan__skeleton" aria-busy="true" />
            )}
            <button
              type="button"
              className="btn btn-primary brokerage-plan__pay"
              disabled={!totals || start.isPending}
              onClick={pay}
            >
              {start.isPending
                ? "Opening checkout…"
                : totals
                  ? `Pay ${money(totals.total)} and start`
                  : "Loading the price…"}
            </button>
            <p className="brokerage-plan__fine">
              Add or remove agents anytime. Changes are prorated.
            </p>
          </aside>
        </div>
      )}
    </section>
  );
};

export default BrokeragePlan;
